// Frame — the app frame's state as ONE product, its transitions as ONE sum,
// and the pure fold that is the ONLY writer of frame factors.
//
// The composition contract (.claude/analysis/composition-contract.md, I2): app
// state is the product of typed region factors, and every cross-factor
// coupling must be a NAMED transition folded exhaustively — never an
// imperative multi-`set` inside a handler. This module is that fold. The host
// holds `FrameState` in a single `useReducer(foldFrame, …)`; handlers dispatch
// `FrameTransition` values and cannot write a factor directly, so a transition
// that moves an unnamed region cannot exist without being visible here.
//
// Each transition variant documents the REGIONS it touches — the unit tests
// assert exactly those fields change and nothing else. The V-catalog's
// measured violations were first reproduced here as named arrows, then fixed
// as values (V6 fell to D4's settings park) — the point of naming them.
//
// WORKSPACE-FLAT (flattened-object-model-design.md): the former `Destination`
// sum (`project` | `workspace`, with a `ParkedFrame` stow/restore pair)
// existed ONLY to distinguish "inside a project" from "the cross-project
// workspace landing." With Project retired as a container there is exactly
// ONE world — the workspace's flat pool — so that whole axis, its park/unpark
// machinery, and the project-keyed maps collapse to their single entry.
//
// Module topology (C1 hotspot decomposition): this entry file keeps
// `initialFrame` and re-exports the substrate from the sibling `frame/` folder:
//  - `frame/types.ts`  — FrameState, the Overlay/Notice unions, FrameTransition
//  - `frame/derive.ts` — pure view projections (dockState/workspaceSurface/…)
//  - `frame/fold.ts`   — the fold helpers, recorder, and `foldFrame`
//
// Discipline (dev-ts): pure — no I/O, no Date/random, no React; named leaf
// types on a `type` discriminant, summed; total fold ending in
// `exhaustSwitchCase`.

import type {
    NavRailState,
    OpenSecondary,
} from "../components/AppShell/AppShell.js";
import { dockConversation } from "../components/Navigation/chatDock.js";
import { landingView } from "../components/Navigation/focusHistory.js";
import {
    tabSetOfPaneTree,
    withFocusedLeafTabs,
} from "../components/Navigation/paneTree.js";
import { emptyClosedTabs } from "../components/Navigation/types.js";
import { focusedViewOf } from "./frame/fold.js";
import { type FrameState, noOverlay, panelOverview } from "./frame/types.js";
import { emptyQueue } from "./messageQueue.js";
import type { ViewState } from "./viewState.js";

// The frame a fresh mount starts from, seeded with the device-persisted view
// state (U46, master audit P12): the open tabs and the active tab survive a
// reload; everything else opens at rest. Whether the conversation owns the
// centre is derived from the restored active tab, so there is no chat
// presentation to seed here.
//
// `openSecondary` is seeded too (J19): the right dock's open/closed state is a
// device preference (dockPreference.ts), remembered across reloads like the
// theme. The pure fold stays policy-free — it accepts whatever seed the impure
// shell hands it; the DEFAULT-OPEN decision lives in the persistence layer. The
// param defaults to `null` (closed) so tests + non-shell callers keep the prior
// at-rest behaviour without threading a preference.
//
// `nav` is seeded the same way (J35): the LEFT rail's expanded/collapsed state
// is a device preference (railPreference.ts), remembered across reloads. Same
// posture as the dock — the fold stays policy-free; the DEFAULT-EXPANDED lives
// in the persistence layer. The param defaults to `"expanded"` so tests +
// non-shell callers keep the prior at-rest behaviour without threading a
// preference.
export const initialFrame = (
    view: ViewState,
    openSecondary: OpenSecondary = null,
    nav: NavRailState = "expanded",
): FrameState => {
    // A restored (or cold-open) active conversation goes to the chat panel;
    // the main view rests on the Library (chatDock.ts).
    const paneTree = withFocusedLeafTabs(
        view.paneTree,
        dockConversation(tabSetOfPaneTree(view.paneTree), { type: "search" }),
    );
    return {
        // EXPANDED at rest by default (owner call 2026-09-29, reversing the
        // 2026-08-20 collapsed default): a fresh mount shows the full left panel.
        // The icon rail remains the collapse affordance (the chevron dispatches
        // `toggle-rail` / `collapse-rail`); the panel is the default, opt-out.
        // Seeded from the device preference (J35, railPreference.ts) so a
        // deliberate collapse survives a reload.
        nav,
        overlay: noOverlay,
        openSecondary,
        // The right panel rests at the conversation overview (openRef cluster
        // phase 1); a teleport / openRef focuses an entity.
        panelFocus: panelOverview,
        anchor: { type: "following" },
        settingsSection: "account",
        // The open project survives a reload (viewState.ts).
        studioProject: view.studioProject,
        studioSurface: "pages",
        paneTree,
        closedTabs: emptyClosedTabs,
        recents: [],
        // Session-only: the window boots focused on the restored active view
        // (cold-open: the nascent seed conversation, which renders as the
        // landing), seeded to MATCH `focusedViewOf` so the recorder no-ops until
        // a real navigation rather than recording a redundant first entry.
        history: {
            back: [],
            current: focusedViewOf(paneTree) ?? landingView,
            forward: [],
        },
        confirmClose: null,
        messages: emptyQueue,
    };
};

// === Public surface — the sibling substrate re-exported at the stable path ===
export type { ChatPlace } from "./frame/derive.js";
export {
    chatPlaceOf,
    cornerInset,
    dockState,
    workspaceSurface,
} from "./frame/derive.js";
export { foldFrame } from "./frame/fold.js";
export type {
    ActivateInPane,
    ActivateTab,
    CloseActiveTab,
    CloseConversationTab,
    CloseOtherTabs,
    CloseOverlay,
    ClosePaneT,
    ClosePreview,
    CloseQualityReport,
    CloseSecondary,
    CloseSettings,
    CloseTab,
    CloseTabsToRight,
    CollapseRail,
    CreateMaterialFromSource,
    CreateOverlay,
    DiscardUnsavedTab,
    DropResourceTab,
    EnterFullChat,
    ExpandRail,
    ExportOverlay,
    FinderOverlay,
    FocusPaneNext,
    FocusPanePrevious,
    FrameState,
    FrameTransition,
    HelpOverlay,
    HideRail,
    HistoryOverlay,
    HostNotice,
    InsertOverlay,
    KeepUnsavedTab,
    NavBack,
    NavForward,
    NewTab,
    NoOverlay,
    OpenOverlay,
    OpenQualityReport,
    OpenResource,
    OpenResourcePreview,
    OpenSettings,
    Overlay,
    OverlaySurface,
    PinChat,
    PromotePreviewTab,
    ReconcileTabs,
    ReopenClosedTab,
    ResizeDividerT,
    ResourceEdited,
    RetitleResource,
    SaveRetry,
    ShowNotice,
    SplitDirection,
    SplitFocusedPane,
    StudioSurface,
    SummonQuality,
    SummonReview,
    SummonSlot,
    ToggleRail,
    UnpinChat,
} from "./frame/types.js";
